import { useSortable } from '@dnd-kit/react/sortable';
import { useState } from 'react';
import type { ReactNode } from 'react';

import type { ActorRule, RoutingTarget, StageTone } from '../../shared/index.js';

import type { BoardWithWorkflow } from '../../api/endpoints.js';
import { Button, CONTROL_FIELD } from '../../components/ui.js';
import { useDepartments, useDirectory, useJobRoles } from '../../hooks/use-directory.js';

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

export function useTargetLabel(): (target: RoutingTarget) => string {
  const { departments } = useDepartments();
  const { jobRoles } = useJobRoles();
  const people = useDirectory();

  return (target: RoutingTarget): string => {
    if (target.kind === 'department') {
      return departments.find((entry) => entry.id === target.departmentId)?.name ?? 'a department';
    }

    if (target.kind === 'position') {
      return jobRoles.find((entry) => entry.id === target.jobRoleId)?.name ?? 'a position';
    }

    const person = people.find((entry) => entry.id === target.userId);
    return person === undefined ? 'somebody' : `${person.firstName} ${person.lastName}`;
  };
}

export function BoardTitle({
  board,
  busy,
  onRename,
}: {
  readonly board: BoardWithWorkflow['board'];
  readonly busy: boolean;
  readonly onRename: (name: string) => void;
}): ReactNode {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(board.name);

  if (!editing) {
    return (
      <>
        <h2 className="text-[16px] font-bold text-navy dark:text-dark-ink">{board.name}</h2>
        <button
          type="button"
          aria-label="Rename board"
          title="Rename board"
          onClick={() => {
            setDraft(board.name);
            setEditing(true);
          }}
          className="grid size-8 shrink-0 place-items-center rounded-field text-muted transition hover:bg-navy/6 hover:text-navy dark:hover:bg-white/10 dark:hover:text-dark-ink"
        >
          <svg
            viewBox="0 0 20 20"
            className="size-4"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M13.5 3.5a1.9 1.9 0 0 1 2.7 2.7L6.5 15.9l-3.2.7.7-3.2 9.5-9.9Z" />
            <path d="M11.8 5.2l2.7 2.7" />
          </svg>
        </button>
      </>
    );
  }

  const ready = draft.trim().length >= 2;

  const save = (): void => {
    if (!ready) return;
    onRename(draft.trim());
    setEditing(false);
  };

  return (
    <>
      <input
        autoFocus
        value={draft}
        aria-label="Board name"
        onChange={(event) => {
          setDraft(event.target.value);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') save();
          if (event.key === 'Escape') setEditing(false);
        }}
        className={`${CONTROL_FIELD} h-9 max-w-[260px]`}
      />
      <Button type="button" size="sm" loading={busy} disabled={!ready} onClick={save}>
        Save
      </Button>
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => {
          setEditing(false);
        }}
      >
        Cancel
      </Button>
    </>
  );
}

export interface StageDraft {
  readonly label: string;
  readonly tone: StageTone;
  readonly resultingStatus: string;
  readonly isTerminal: boolean;
  readonly afterStageId: string;
}

export const BLANK_STAGE: StageDraft = {
  label: '',
  tone: 'neutral',
  resultingStatus: 'open',
  isTerminal: false,
  afterStageId: '',
};

export interface MoveDraft {
  readonly label: string;
  readonly fromStageId: string;
  readonly toStageId: string;
  readonly actorRule: ActorRule;
  readonly actorDepartmentId: string;
  readonly forbidSelfService: boolean;
  readonly handOffTarget: RoutingTarget | null;
}

export const BLANK_MOVE: MoveDraft = {
  label: '',
  fromStageId: '',
  toStageId: '',
  actorRule: 'anyone_involved',
  actorDepartmentId: '',
  forbidSelfService: false,
  handOffTarget: null,
};

export const VIEW_HINT = (
  <>
    <p>Who can open this board at all. Leave it empty and everyone signed in can.</p>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">It does not widen what
      they see on it.</strong>{' '}
      Which requests appear is decided the same way everywhere: the departments somebody works
      in, requests they raised, and anything they have had a hand in. A board can open to
      somebody and still show them very little.
    </p>
    <p>
      Anyone who may raise a request here can open it regardless — a board you can file into but
      cannot see would be unusable.
    </p>
  </>
);

export const INTAKE_HINT = (
  <>
    <p>
      Where a new request goes the moment it is raised, and who it belongs to when it gets
      there.
    </p>
    <ul className="ml-4 list-disc space-y-1">
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Department</strong> —
        lands in that queue, on one of the people who work there.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Position</strong> — lands
        in that position's department, on one of the people holding it.
      </li>
      <li>
        <strong className="font-semibold text-navy dark:text-dark-ink">Person</strong> — lands in
        their department, on them.
      </li>
    </ul>
    <p>
      <strong className="font-semibold text-navy dark:text-dark-ink">A request always ends up on
      somebody.</strong>{' '}
      Where several people qualify, one is picked at random — naming a department means "any of
      them", not "nobody in particular", and a request nobody owns is how work goes quiet.
    </p>
    <p>
      Leave it on "the requester picks" and whoever raises the request chooses the department
      themselves — which is what the everyday board does.
    </p>
  </>
);

export const RAISE_HINT = (
  <>
    <p>Who can start a new request here. Leave it empty and everyone signed in can.</p>
    <p>
      Start with a department for the whole of it. Narrow to positions when only certain jobs
      should, and to people when it really is a named few.
    </p>
  </>
);

export function SortableStageRow({
  id,
  index,
  children,
}: {
  readonly id: string;
  readonly index: number;
  readonly children: ReactNode;
}): ReactNode {
  const { ref, handleRef, isDragging } = useSortable({
    id,
    index,
    type: 'stage',
    accept: ['stage'],
  });

  return (
    <div
      ref={ref}
      className={`flex flex-wrap items-center gap-3 rounded-panel border border-hairline bg-white px-3.5 py-2.5 dark:border-dark-line dark:bg-dark-panel ${
        isDragging ? 'opacity-60 shadow-menu' : ''
      }`}
    >
      <button
        ref={handleRef}
        type="button"
        aria-label="Drag to reorder this column"
        className="cursor-grab touch-none text-muted transition hover:text-navy active:cursor-grabbing dark:hover:text-dark-ink"
      >
        <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
          <circle cx="6" cy="4" r="1.3" />
          <circle cx="10" cy="4" r="1.3" />
          <circle cx="6" cy="8" r="1.3" />
          <circle cx="10" cy="8" r="1.3" />
          <circle cx="6" cy="12" r="1.3" />
          <circle cx="10" cy="12" r="1.3" />
        </svg>
      </button>
      {children}
    </div>
  );
}

export interface ConfirmDeleteBoardProps {
  readonly name: string;
  readonly ticketCount: number;
  readonly liveTicketCount: number;
  readonly pending: boolean;
  readonly onCancel: () => void;
  readonly onConfirm: () => void;
}

export function ConfirmDeleteBoard({
  name,
  ticketCount,
  liveTicketCount,
  pending,
  onCancel,
  onConfirm,
}: ConfirmDeleteBoardProps): ReactNode {
  const blocked = liveTicketCount > 0;
  const requests = `${String(liveTicketCount)} ${liveTicketCount === 1 ? 'request' : 'requests'}`;
  const carried = `${String(ticketCount)} ${ticketCount === 1 ? 'request' : 'requests'}`;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-end bg-navy-deep/45 backdrop-blur-sm sm:place-items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-delete-board-title"
    >
      <div className="w-full overflow-hidden rounded-t-card bg-white shadow-menu sm:max-w-[480px] sm:rounded-card dark:bg-dark-panel">
        <div className="h-1 bg-linear-to-r from-gold via-cyan to-royal" />

        <div className="p-5 sm:p-6">
          <h2
            id="confirm-delete-board-title"
            className="font-display text-[20px] font-extrabold tracking-[-0.02em] text-navy dark:text-dark-ink"
          >
            {blocked ? `"${name}" still has work on it` : `Delete "${name}"?`}
          </h2>

          <p className="mt-2 text-[14px] leading-relaxed text-navy/80 dark:text-dark-ink/80">
            {blocked ? (
              <>
                This board still has <strong>{requests}</strong> in progress. Close or archive them
                first — a board cannot be deleted while work is live on it.
              </>
            ) : ticketCount > 0 ? (
              <>
                This deletes <strong>{carried}</strong> along with the board — closed and archived
                alike, with their history, comments and attachments. They cannot be recovered
                afterwards.
              </>
            ) : (
              <>
                The board has no requests. Its columns and moves are deleted with it, and this
                cannot be undone.
              </>
            )}
          </p>

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="secondary" onClick={onCancel}>
              {blocked ? 'Close' : 'Cancel'}
            </Button>
            {blocked ? null : (
              <Button type="button" variant="danger" loading={pending} onClick={onConfirm}>
                Delete board
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
